import { PANEL_NAME_KEY, TRIP_STATE } from '@allocab/allocab-commons-shared-types';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { TripCard } from './TripCard';
import { DEPARTURE_AT_FILTER_FORMAT, DEPARTURE_DAY_SEARCH_PARAMS_KEY } from './TripDepartureAtFilter';
import { PANEL_SEARCH_PARAMS_KEY } from './TripPanelFilter';
import { STATE_SEARCH_PARAMS_KEY } from './TripStateFilter';
import { TO_ASSIGN_SEARCH_PARAMS_KEY } from './TripToAssignFilter';
import { PrimaryButton } from '../../components/button/PrimaryButton';
import { SideBarFilter } from '../../components/layout/side-bar/SideBarFilter';
import { Spinner } from '../../components/Spinner';
import { useInfiniteFetchTrips } from '../../hooks/useFetchTrips';
import { useFetchUser } from '../../hooks/useFetchUser';
import { t } from '../../services/translation/translation';

type Props = {
  driverKey?: string;
};

export const TripCards = ({ driverKey }: Props) => {
  const [searchParams] = useSearchParams();
  const orderBy = searchParams.get('orderBy') ?? 'resume.departureAt';
  const direction = (searchParams.get('direction') ?? 'desc') as 'desc' | 'asc';
  const state = (searchParams.get(STATE_SEARCH_PARAMS_KEY)?.split(',') ?? undefined) as TRIP_STATE[] | undefined;
  const panelKey = (searchParams.get(PANEL_SEARCH_PARAMS_KEY)?.split(',') ?? undefined) as PANEL_NAME_KEY[] | undefined;
  const departureDay = searchParams.get(DEPARTURE_DAY_SEARCH_PARAMS_KEY);
  const toAssign = searchParams.get(TO_ASSIGN_SEARCH_PARAMS_KEY);
  const { data: user } = useFetchUser();
  const { data, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } = useInfiniteFetchTrips({
    direction,
    orderBy,
    state,
    panelKey,
    ...(driverKey
      ? { driverKey: [driverKey] }
      : toAssign === 'true' && user?.driverKey
      ? { driverKey: [user.driverKey], state: ['INCOMING'] }
      : {}),
    ...(departureDay
      ? {
          interval: {
            start: dayjs.tz(departureDay, DEPARTURE_AT_FILTER_FORMAT, 'Europe/Paris').startOf('day').valueOf(),
            end: dayjs.tz(departureDay, DEPARTURE_AT_FILTER_FORMAT, 'Europe/Paris').endOf('day').valueOf(),
          },
        }
      : {}),
  });

  const trips = useMemo(() => data?.pages.map(p => p.items).flat() || [], [data]);

  return (
    <div>
      <SideBarFilter isAssignable={driverKey ? false : true} />
      <div>
        <div className="space-y-2">
          {trips.map(trip => (
            <TripCard isAssignable={driverKey ? false : true} key={trip.id} trip={trip} />
          ))}
        </div>

        <div>
          {hasNextPage ? (
            <div className="flex justify-center mt-3">
              <PrimaryButton
                onClick={() => {
                  fetchNextPage();
                }}
                isLoading={isFetchingNextPage}
              >
                {t('common.see-more')}
              </PrimaryButton>
            </div>
          ) : null}
          <div>{isLoading ? <Spinner /> : null}</div>
        </div>
      </div>
    </div>
  );
};
